import { createRouter } from '@tanstack/react-router';
import { parsePlainSearch, stringifyPlainSearch } from '@voll-intelligence/domain';

import { useSidebarCollapsed } from '@web/data/repositories';
import { NotFoundPage, WorkspaceShellSkeleton } from '@web/ui/core';

import {
  alertsRoute,
  authCallbackRoute,
  docsRoute,
  indexRoute,
  loginRoute,
  miceNewProjectRoute,
  miceProjectRoute,
  miceProjectsRoute,
  noOrgRoute,
  orgRoute,
  overviewIndexRoute,
  projectEditLayoutRoute,
  projectIdentityRoute,
  projectNewIdentityRoute,
  projectNewLayoutRoute,
  projectSettingsRoute,
  projectSquadsRoute,
  projectUsersRoute,
  protectedRoute,
  rootRoute,
  settingsApiKeysRoute,
  settingsIntegrationsRoute,
  settingsBoardConfigRoute,
  settingsIndexRoute,
  settingsLayoutRoute,
  settingsMembersRoute,
  settingsProfileRoute,
  settingsTriageRoute,
  orgWorkspaceLayoutRoute,
  squadBoardCardTabRoute,
  squadBoardCardTaskRoute,
  squadBoardRoute,
  squadEditLayoutRoute,
  squadIdentityRoute,
  squadNewIdentityRoute,
  squadNewLayoutRoute,
  squadSettingsRoute,
  customerIndexRoute,
  customerRoute,
  customerTabRoute,
  scheduleChangesRoute,
  triageCardIndexRoute,
  triageCardRoute,
  triageCardTabRoute,
  triageCardTaskRoute,
  triageIndexRoute,
  travelerAccessRoute,
  travelerViewRoute,
  viewAsRoute,
} from '@web/core/routes';

const routeTree = rootRoute.addChildren([
  loginRoute,
  authCallbackRoute,
  protectedRoute.addChildren([
    indexRoute,
    noOrgRoute,
    docsRoute,
    orgRoute.addChildren([
      orgWorkspaceLayoutRoute.addChildren([
        overviewIndexRoute,
        triageIndexRoute.addChildren([
          triageCardRoute.addChildren([
            triageCardIndexRoute,
            triageCardTabRoute.addChildren([triageCardTaskRoute]),
          ]),
        ]),
        alertsRoute,
        miceProjectsRoute,
        miceNewProjectRoute,
        miceProjectRoute,
        customerRoute.addChildren([customerIndexRoute, customerTabRoute]),
        projectNewLayoutRoute.addChildren([projectNewIdentityRoute]),
        projectEditLayoutRoute.addChildren([
          projectIdentityRoute,
          projectSquadsRoute,
          projectUsersRoute,
          projectSettingsRoute,
        ]),
        squadBoardRoute.addChildren([
          squadBoardCardTabRoute.addChildren([squadBoardCardTaskRoute]),
        ]),
        squadNewLayoutRoute.addChildren([squadNewIdentityRoute]),
        squadEditLayoutRoute.addChildren([squadIdentityRoute, squadSettingsRoute]),
      ]),
      settingsLayoutRoute.addChildren([
        settingsIndexRoute,
        settingsProfileRoute,
        settingsMembersRoute,
        settingsApiKeysRoute,
        settingsIntegrationsRoute,
        settingsBoardConfigRoute,
        settingsTriageRoute,
        scheduleChangesRoute,
        travelerAccessRoute,
      ]),
      travelerViewRoute,
      viewAsRoute,
    ]),
  ]),
]);

/**
 * Pending fallback for every route. Lives here (not in `ui/core`) because it
 * reads the sidebar store — `ui/core` organisms stay presentational.
 */
function RouterPendingShell(): React.JSX.Element {
  const collapsed = useSidebarCollapsed((s) => s.collapsed);
  return <WorkspaceShellSkeleton collapsed={collapsed} />;
}

export const router = createRouter({
  routeTree,
  // Search as plain text (#4974): the routes' validators decide what a value means.
  parseSearch: parsePlainSearch,
  stringifySearch: stringifyPlainSearch,
  defaultPreload: 'intent',
  defaultNotFoundComponent: NotFoundPage,
  // Cold loads wait on the `protectedRoute` → `orgRoute` loaders; without a
  // pending component the viewport stays blank meanwhile. 150ms skips warm
  // navigations (caches already primed); 300ms min stops the skeleton flashing.
  defaultPendingComponent: RouterPendingShell,
  defaultPendingMs: 150,
  defaultPendingMinMs: 300,
});
